import React, { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  CalendarLayoutAssignmentFilter,
  CalendarLayoutAssignmentRowOutDTO,
  Pageable,
  PageResponseCalendarLayoutAssignmentRowOutDTO,
} from '@api/ringil-api.v2';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { REMOVE } from './assignmentActions';
import { useAdminCompanyOptions } from './useAdminCompanyOptions';

/** Rows are real assignments here, but the system-default row can come back without one. */
type Row = CalendarLayoutAssignmentRowOutDTO & { id: string; assignmentId?: string };

const rowKey = (row: CalendarLayoutAssignmentRowOutDTO): string =>
  row.id ?? `${row.calendarLayoutScopeType}:${row.companyId ?? ''}:${row.locationId ?? ''}`;

interface ModelAssignmentsTabProps {
  modelId: string;
  admin: boolean;
  /** False hides every removal control — a customer looking at a system-wide model. */
  editable: boolean;
  /** Bumped by the owner when an assignment is made elsewhere on the screen. */
  refreshToken?: number;
  onChanged?: () => void;
  /** Reports the row count on every load, so the tab label can show a number that is actually current. */
  onTotalChange?: (total: number) => void;
}

/**
 * Where this model is applied, and the only place to take it back off.
 *
 * Scoped to one model (`modelId` is forced into the filter), unlike the management screen's
 * assignments tab which spans every model.
 */
export const ModelAssignmentsTab: React.FC<ModelAssignmentsTabProps> = ({ modelId, admin, editable, refreshToken, onChanged, onTotalChange }) => {
  const { tr } = useTranslationLgs();
  const { listAssignments, applyAssignments } = useContext(ApiContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: Row[]; clear: () => void }>(null);
  // The company filter is a list of real companies rather than a text box. Admin-only: a customer
  // sees one company here, so filtering by it is noise.
  const { options: companyOptions } = useAdminCompanyOptions(admin, 'all');
  const [busy, setBusy] = useState(false);

  const loadData = (filter: CalendarLayoutAssignmentFilter, pageable: Pageable, onSuccess: (paged: PagedData<Row>) => void) => {
    listAssignments(
      pageable,
      // Forced to this model; the listing itself only returns real assignments.
      { ...filter, modelId },
      (response: PageResponseCalendarLayoutAssignmentRowOutDTO) => {
        onTotalChange?.(response.total);
        onSuccess({
          data: response.data.map(row => ({ ...row, assignmentId: row.id, id: rowKey(row) })),
          total: response.total,
          page: response.page,
          size: response.size,
        });
      },
    );
  };

  const removeRows = (rows: Row[]) => {
    const targets = rows
      .filter(row => row.assignmentId && row.calendarLayoutScopeType !== 'SYSTEM_DEFAULT')
      .map(row => ({ calendarLayoutAction: REMOVE, companyId: row.companyId, locationId: row.locationId }));
    if (targets.length === 0) return;
    setBusy(true);
    applyAssignments(
      { replaceExisting: true, targets },
      () => {
        setBusy(false);
        tableRef.current?.clear();
        tableRef.current?.reloadData();
        onChanged?.();
      },
      undefined,
      { onError: () => setBusy(false) },
    );
  };

  const columnsConfig: ColumnConfig<Row>[] = [
    {
      id: 'company',
      header: 'CalendarLayout.column.company',
      headerDefault: 'Company',
      width: '32%',
      sortable: false,
      order: 1,
      verticalAlign: 'middle',
      render: row => <span className="font-bold">{row.companyName ?? tr('CalendarLayout.scopeSystem', 'System default')}</span>,
    },
    {
      id: 'location',
      header: 'CalendarLayout.column.location',
      headerDefault: 'Location',
      width: '32%',
      sortable: false,
      order: 2,
      verticalAlign: 'middle',
      render: row => (
        <span className="text-sm">
          {row.locationName ?? (row.calendarLayoutScopeType === 'COMPANY' ? tr('CalendarLayout.wholeCompany', 'Whole company') : '—')}
        </span>
      ),
    },
    {
      id: 'scope',
      header: 'CalendarLayout.column.scope',
      headerDefault: 'Scope',
      width: '18%',
      sortable: false,
      order: 3,
      verticalAlign: 'middle',
      render: row => <span className="text-xs text-content-secondary">{row.calendarLayoutScopeType}</span>,
    },
    {
      id: 'actions',
      header: 'CalendarLayout.column.actions',
      headerDefault: '',
      width: '18%',
      sortable: false,
      order: 4,
      verticalAlign: 'middle',
      render: row =>
        // The system default is not an override, so there is nothing to remove here — it is changed
        // on the models list with "Set as default".
        editable && row.assignmentId && row.calendarLayoutScopeType !== 'SYSTEM_DEFAULT' ? (
          <RingilButton label={tr('CalendarLayout.removeOverride', 'Remove')} mode="table" disabled={busy} onClick={() => removeRows([row])} />
        ) : null,
    },
  ];

  const massActionConfig: MassActionConfig<Row>[] = editable
    ? [
        {
          label: tr('CalendarLayout.removeSelected', 'Remove selected'),
          onClick: () => removeRows(tableRef.current?.selectedObjects ?? []),
          disabled: selected => selected.every(row => !row.assignmentId || row.calendarLayoutScopeType === 'SYSTEM_DEFAULT'),
        },
      ]
    : [];

  const filtersConf: FilterConfig[] = [
    ...(admin
      ? [
          {
            filterType: FilterType.Dropdown,
            placeholder: tr('CalendarLayout.filter.company', 'Company'),
            value: ['companyId'],
            options: companyOptions,
          } as FilterConfig,
        ]
      : []),
    { filterType: FilterType.InputText, placeholder: tr('CalendarLayout.filter.location', 'Location'), value: ['q'] },
  ];

  return (
    <RingilDataTable
      key={refreshToken}
      ref={tableRef}
      columnConfig={columnsConfig as ColumnConfig<unknown>[]}
      loadDataFromApi={loadData}
      filterName={`calendar_layout_model_assignments_${admin ? 'admin' : 'company'}`}
      filtersConf={filtersConf}
      massActionConfig={massActionConfig as MassActionConfig<unknown>[]}
      // Rows without a selection checkbox would still join "select all" and then be silently
      // dropped by removeRows; excluding them keeps the count honest.
      isRowSelectable={row => !!(row as Row).assignmentId && (row as Row).calendarLayoutScopeType !== 'SYSTEM_DEFAULT'}
    />
  );
};
